import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box, Typography, Alert, Grid, ToggleButtonGroup, ToggleButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
  Menu, MenuItem, ListItemIcon, ListItemText, Divider, Slide, Paper,
  Button, Chip, FormControl, InputLabel, Select, TextField, Switch,
  FormControlLabel,
} from '@mui/material'
import {
  TableChart, ViewModule, ViewList, ContentCopy, Edit,
  Archive, Delete, CompareArrows, FilterList,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { usePlantContext } from '../hooks/usePlantContext'
import { api } from '../services/api'
import LoadingState from '../components/common/LoadingState'
import PlanCard from '../components/plans/PlanCard'
import PlanListRow from '../components/plans/PlanListRow'
import SplitButton from '../components/plans/SplitButton'
import FolderSelector from '../components/plans/FolderSelector'
import CreatePlanModal from '../components/plans/CreatePlanModal'
import CopyPlanModal from '../components/plans/CopyPlanModal'

const DEMO_FOLDER = { id: 'demo-folder-1', name: '2026-03-17', plant: 'FBO' }

const DEMO_PLANS = [
  {
    id: 'demo-plan-1',
    name: 'Budget 2026 — Base Plan',
    description: 'Annual budget base plan for FBO plant covering all product lines.',
    plant: 'FBO',
    status: 'APPROVED',
    isProductionPlan: true,
    createdBy: 'melody.simon@porticafoods.com',
    createdAt: '2026-03-09T14:30:00Z',
    modificationCount: 3,
    totalCases12mo: 12900000,
    totalECs12mo: 10600000,
    folderId: 'demo-folder-1',
  },
  {
    id: 'demo-plan-2',
    name: 'GMI Soups — Full Volume',
    description: 'Full volume scenario for GMI soups contract across all SKUs.',
    plant: 'FBO',
    status: 'DRAFT',
    isProductionPlan: false,
    createdBy: 'james.wheeler@porticafoods.com',
    createdAt: '2026-03-12T09:15:00Z',
    modificationCount: 1,
    totalCases12mo: 4200000,
    totalECs12mo: 3800000,
    folderId: 'demo-folder-1',
  },
  {
    id: 'demo-plan-3',
    name: 'GMI Soups — Phase 1 Only',
    description: 'Phase 1 only scenario with reduced SKU set for initial contract period.',
    plant: 'FBO',
    status: 'DRAFT',
    isProductionPlan: false,
    createdBy: 'james.wheeler@porticafoods.com',
    createdAt: '2026-03-14T16:45:00Z',
    modificationCount: 0,
    totalCases12mo: 2100000,
    totalECs12mo: 1900000,
    folderId: 'demo-folder-1',
  },
]

const SORT_OPTIONS = [
  { key: 'createdAt', label: 'Date' },
  { key: 'name', label: 'Name' },
  { key: 'status', label: 'Status' },
  { key: 'plant', label: 'Plant' },
]

function getViewPref() {
  try { return localStorage.getItem('planListView') || 'card' } catch { return 'card' }
}
function setViewPref(v) {
  try { localStorage.setItem('planListView', v) } catch { /* noop */ }
}

export default function PlanList() {
  const navigate = useNavigate()
  const { plant } = usePlantContext()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [plans, setPlans] = useState([])
  const [folders, setFolders] = useState([])
  const [selectedFolderId, setSelectedFolderId] = useState('')
  const [view, setView] = useState(getViewPref)
  const [selected, setSelected] = useState(new Set())
  const [sortKey, setSortKey] = useState('createdAt')
  const [sortDir, setSortDir] = useState('desc')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [prodPlanOnly, setProdPlanOnly] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [contextMenu, setContextMenu] = useState(null)
  const [contextPlan, setContextPlan] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [copyOpen, setCopyOpen] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [renamingPlan, setRenamingPlan] = useState(null)
  const [renameValue, setRenameValue] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [plansData, foldersData] = await Promise.all([
        api.get(`/plans?plant=${plant.plantCode}${selectedFolderId ? `&folderId=${selectedFolderId}` : ''}`).catch(() => null),
        api.get(`/folders?plant=${plant.plantCode}`).catch(() => null),
      ])
      setPlans(plansData?.length ? plansData : DEMO_PLANS)
      setFolders(foldersData?.length ? foldersData : [DEMO_FOLDER])
    } catch {
      setPlans(DEMO_PLANS)
      setFolders([DEMO_FOLDER])
    } finally {
      setLoading(false)
    }
  }, [plant.plantCode, selectedFolderId])

  useEffect(() => { loadData() }, [loadData])

  const handleViewChange = (_, v) => {
    if (!v) return
    setView(v)
    setViewPref(v)
  }

  const handleSelect = (plan, checked) => {
    setSelected((prev) => {
      const next = new Set(prev)
      checked ? next.add(plan.id) : next.delete(plan.id)
      return next
    })
  }

  const handleOpen = (planId) => navigate(`/plans/${planId}/grid`)

  const handleContextMenu = (e, plan) => {
    e.preventDefault?.()
    e.stopPropagation?.()
    setContextMenu({ x: e.clientX ?? e.pageX, y: e.clientY ?? e.pageY })
    setContextPlan(plan)
  }

  const closeContext = () => { setContextMenu(null); setContextPlan(null) }

  const handleDuplicate = async () => {
    if (!contextPlan) return
    closeContext()
    try {
      await api.post('/plans/copy', { sourcePlanId: contextPlan.id, name: `${contextPlan.name} — Copy` })
      loadData()
    } catch { /* handled by useApi */ }
  }

  const handleRenameStart = () => {
    if (!contextPlan) return
    setRenamingPlan(contextPlan.id)
    setRenameValue(contextPlan.name)
    closeContext()
  }

  const handleRenameConfirm = async (planId) => {
    const trimmed = renameValue.trim()
    if (!trimmed) { setRenamingPlan(null); return }
    setPlans((prev) => prev.map((p) => p.id === planId ? { ...p, name: trimmed } : p))
    setRenamingPlan(null)
    try {
      const { put } = await import('../hooks/useApi').then(() => import('../services/api'))
      await put(`/plans/${planId}`, { name: trimmed })
    } catch { /* optimistic update stays */ }
  }

  const handleArchive = async () => {
    if (!contextPlan) return
    closeContext()
    setPlans((prev) => prev.filter((p) => p.id !== contextPlan.id))
  }

  const handleDelete = async () => {
    if (!contextPlan) return
    closeContext()
    setPlans((prev) => prev.filter((p) => p.id !== contextPlan.id))
  }

  const handleCreatePlan = async (data) => {
    setCreateError(null)
    setSaving(true)
    try {
      await api.post('/plans', data)
      setCreateOpen(false)
      setSaving(false)
      loadData()
    } catch (err) {
      setSaving(false)
      setCreateError(err.message)
    }
  }

  const handleCopyPlan = async (data) => {
    setCreateError(null)
    setSaving(true)
    try {
      await api.post('/plans/copy', data)
      setCopyOpen(false)
      setSaving(false)
      loadData()
    } catch (err) {
      setSaving(false)
      setCreateError(err.message)
    }
  }

  const handleCreateFolder = async (name) => {
    const newFolder = { id: `folder-${Date.now()}`, name, plant: plant.plantCode }
    setFolders((prev) => [...prev, newFolder])
    setSelectedFolderId(newFolder.id)
    try { await api.post('/folders', { name, plant: plant.plantCode }) } catch { /* keep optimistic */ }
  }

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const filteredPlans = useMemo(() => {
    let result = [...plans]
    if (statusFilter === 'APPROVED') result = result.filter((p) => p.status === 'APPROVED')
    if (statusFilter === 'NOT_APPROVED') result = result.filter((p) => p.status !== 'APPROVED')
    if (prodPlanOnly) result = result.filter((p) => p.isProductionPlan)

    result.sort((a, b) => {
      let cmp = 0
      if (sortKey === 'createdAt') cmp = new Date(a.createdAt) - new Date(b.createdAt)
      else if (sortKey === 'name') cmp = a.name.localeCompare(b.name)
      else if (sortKey === 'status') cmp = a.status.localeCompare(b.status)
      else if (sortKey === 'plant') cmp = a.plant.localeCompare(b.plant)
      return sortDir === 'desc' ? -cmp : cmp
    })
    return result
  }, [plans, statusFilter, prodPlanOnly, sortKey, sortDir])

  const compareCount = selected.size

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <TableChart color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Plans</Typography>
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <FolderSelector
          folders={folders}
          selectedFolderId={selectedFolderId}
          onSelect={setSelectedFolderId}
          onCreateFolder={handleCreateFolder}
        />

        <Box sx={{ flex: 1 }} />

        <Button
          size="small"
          variant={showFilters ? 'contained' : 'outlined'}
          startIcon={<FilterList />}
          onClick={() => setShowFilters((v) => !v)}
        >
          Filters
        </Button>

        <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small">
          <ToggleButton value="card"><ViewModule /></ToggleButton>
          <ToggleButton value="list"><ViewList /></ToggleButton>
        </ToggleButtonGroup>

        <SplitButton
          onCreateNew={() => setCreateOpen(true)}
          onCopyFromPlan={() => setCopyOpen(true)}
        />
      </Box>

      {/* Filter bar */}
      {showFilters && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="NOT_APPROVED">Not Approved</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={prodPlanOnly} onChange={(e) => setProdPlanOnly(e.target.checked)} size="small" />}
            label="Production Plans only"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort by</InputLabel>
            <Select value={sortKey} label="Sort by" onChange={(e) => { setSortKey(e.target.value); setSortDir('asc') }}>
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingState message="Loading plans..." />}

      {/* Card View */}
      {!loading && view === 'card' && (
        <Grid container spacing={2}>
          {filteredPlans.map((plan) => (
            <Grid key={plan.id} size={{ xs: 12, sm: 6, md: 4 }}>
              {renamingPlan === plan.id ? (
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRenameConfirm(plan.id)
                    if (e.key === 'Escape') setRenamingPlan(null)
                  }}
                  onBlur={() => handleRenameConfirm(plan.id)}
                />
              ) : (
                <PlanCard
                  plan={plan}
                  selected={selected.has(plan.id)}
                  onSelect={handleSelect}
                  onOpen={handleOpen}
                  onContextMenu={handleContextMenu}
                />
              )}
            </Grid>
          ))}
          {filteredPlans.length === 0 && !loading && (
            <Grid size={12}>
              <Typography variant="body1" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                No plans found. Create your first plan to get started.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {/* List View */}
      {!loading && view === 'list' && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'plant', label: 'Plant' },
                  { key: 'status', label: 'Status' },
                  { key: null, label: 'Prod. Plan', align: 'center' },
                  { key: 'createdAt', label: 'Created' },
                  { key: null, label: 'Mods', align: 'right' },
                  { key: null, label: 'Cases', align: 'right' },
                  { key: null, label: 'ECs', align: 'right' },
                  { key: null, label: '', align: 'right' },
                ].map((col, i) => (
                  <TableCell key={i} align={col.align}>
                    {col.key ? (
                      <TableSortLabel
                        active={sortKey === col.key}
                        direction={sortKey === col.key ? sortDir : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlans.map((plan) => (
                <PlanListRow
                  key={plan.id}
                  plan={plan}
                  selected={selected.has(plan.id)}
                  onSelect={handleSelect}
                  onOpen={handleOpen}
                  onContextMenu={handleContextMenu}
                />
              ))}
              {filteredPlans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No plans found. Create your first plan to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Floating Compare Bar */}
      <Slide direction="up" in={compareCount >= 2} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
            borderRadius: 3, zIndex: 1200,
          }}
        >
          <CompareArrows color="primary" />
          <Typography variant="body2">
            {compareCount} plans selected
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<CompareArrows />}
            onClick={() => {
              const ids = [...selected].join(',')
              navigate(`/reports/comparison?plans=${ids}`)
            }}
          >
            Compare
          </Button>
          <Button size="small" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </Paper>
      </Slide>

      {/* Context Menu */}
      <Menu
        open={!!contextMenu}
        onClose={closeContext}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        <MenuItem onClick={handleDuplicate}>
          <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleRenameStart}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleArchive}>
          <ListItemIcon><Archive fontSize="small" /></ListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Modals */}
      <CreatePlanModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setCreateError(null) }}
        onSubmit={handleCreatePlan}
        folders={folders}
        onCreateFolder={handleCreateFolder}
        loading={saving}
        error={createError}
      />
      <CopyPlanModal
        open={copyOpen}
        onClose={() => { setCopyOpen(false); setCreateError(null) }}
        onSubmit={handleCopyPlan}
        plans={plans}
        loading={saving}
        error={createError}
        currentPlant={plant.plantCode}
      />
    </Box>
  )
}
